"use client";

import React, { useEffect, useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import ImageUploader from "./ImageUploader";
import SellerPhotoCoach from "@/components/listing/SellerPhotoCoach";
import { DEV_IMAGE_VALIDATION_PREVIEW_COPY } from "@/lib/listing/devImageValidationPreviewCopy";
import useItemImages from "@/lib/hooks/useItemImages";
import { ItemImage } from "@/lib/types";

interface ItemImageManagerProps {
  itemId?: string;
  images?: ItemImage[];
  onChange?: (images: ItemImage[]) => void;
  maxImages?: number;
  minImages?: number;
  forcePermission?: boolean; // Allow forcing permission for new item creation
  previewMode?: boolean; // Add this prop to prevent immediate database changes
  /** Dev toolbox: show static copy for warn/block photo gate previews. */
  devImageValidationPreview?: "warn" | "block" | null;
}

export default function ItemImageManager({
  itemId,
  images: externalImages,
  onChange,
  maxImages = 3, // Maximum of 3 images per item
  minImages = 0,
  forcePermission = true,
  previewMode = false, // Default to false for backward compatibility
  devImageValidationPreview = null,
}: ItemImageManagerProps) {
  /**
   * Local state to hold images when not controlled externally
   */
  const [localImages, setLocalImages] = useState<ItemImage[]>(
    externalImages || [],
  );

  /**
   * Sync localImages whenever externalImages prop changes.
   * Ensures proper handling in controlled component usage.
   */
  useEffect(() => {
    if (externalImages) {
      const timer = setTimeout(() => {
        setLocalImages(externalImages);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [externalImages]);

  /**
   * Helper function to safely call onChange outside render cycle
   */
  const safeOnChange = (images: ItemImage[]) => {
    if (onChange) {
      // Use setTimeout to defer the call outside the render cycle
      setTimeout(() => onChange(images), 0);
    }
  };

  /**
   * Hook to fetch images and permissions if itemId is available
   */
  const {
    images: fetchedImages,
    mainImage,
    isLoading,
    error,
    refetch,
    canManage,
  } = useItemImages({
    itemId: itemId || "",
    forcePermission,
  });

  /**
   * Select appropriate image source:
   * - If external images are provided, use them (controlled mode).
   * - Else if itemId exists and we're not loading, use fetched images.
   * - Else use local images (uncontrolled mode).
   */
  const images =
    externalImages ?? (itemId && !isLoading ? fetchedImages : localImages);

  /**
   * Handles a new image upload
   */
  const handleImageUploaded = async (image: ItemImage) => {
    // Use functional state update to ensure we have the latest state
    setLocalImages((currentImages) => {
      const newImages = [...currentImages];
      const exists = newImages.some((img) => img.id === image.id);

      if (!exists) {
        newImages.push(image);
        // Safely call onChange outside render cycle
        safeOnChange(newImages);
        return newImages;
      } else {
        return currentImages; // Return unchanged if image already exists
      }
    });
  };

  /**
   * Handles deletion of an image
   */
  const handleImageDeleted = async (
    imageId: string,
    action: "mark" | "unmark" | "delete" = "delete",
  ) => {
    const currentImages = images ?? [];

    if (action === "mark" || action === "unmark") {
      const shouldMark = action === "mark";
      const newImages = currentImages.map((img) =>
        img.id === imageId
          ? {
              ...img,
              metadata: {
                ...(img.metadata || {}),
                __pendingDelete: shouldMark,
              },
            }
          : img,
      );
      setLocalImages(newImages);
      safeOnChange(newImages);
      return;
    }

    const newImages = currentImages.filter((img) => img.id !== imageId);
    setLocalImages(newImages);
    safeOnChange(newImages);
  };

  /**
   * Handles setting an image as the main/primary image
   */
  const handleMainImageChanged = async (imageId: string) => {
    const currentImages = images ?? [];
    const newImages = currentImages.map((img) => ({
      ...img,
      isMain: img.id === imageId,
    }));

    setLocalImages(newImages);
    safeOnChange(newImages);
  };

  const activeImagesCount = (images || []).filter(
    (img) => img.metadata?.__pendingDelete !== true,
  ).length;

  return (
    <div className="space-y-2">
      {/* Header section with refresh */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          Item Images{" "}
          <span className="text-sm text-text-light">(exactly 3 required)</span>{" "}
          <span className="font-normal text-red-500">*</span>
        </h2>
        {itemId && isLoading && !previewMode ? (
          <ArrowPathIcon className="h-5 w-5 animate-spin text-text-light" />
        ) : (
          itemId &&
          !previewMode && (
            <button
              type="button"
              onClick={refetch}
              className="text-sm text-blue-500 hover:text-blue-700 flex items-center gap-1"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Refresh
            </button>
          )
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 p-3 rounded-md">
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      )}

      {/* Permission warning */}
      {itemId && !canManage && !isLoading && !forcePermission && (
        <div className="bg-amber-50 p-3 rounded-md">
          <p className="text-amber-600 text-sm">
            You do not have permission to manage images for this item.
            {images?.length > 0
              ? " You can view the images, but not modify them."
              : ""}
          </p>
        </div>
      )}

      {/* Image count and main image info */}
      <div className="text-sm text-text-light">
        {activeImagesCount} of {maxImages} images
        {minImages > 0 && (
          <span className="ml-1">(minimum {minImages} required)</span>
        )}
        {mainImage && (
          <span className="ml-2">
            (Main image: {mainImage.caption || "Untitled"})
          </span>
        )}
      </div>

      {process.env.NODE_ENV === "development" && devImageValidationPreview ? (
        <div
          className={`rounded-lg border p-3 text-sm ${
            devImageValidationPreview === "block"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-amber-200 bg-amber-50 text-amber-950"
          }`}
          role="status"
        >
          <p className="font-medium">
            {DEV_IMAGE_VALIDATION_PREVIEW_COPY[devImageValidationPreview].title}
          </p>
          <p className="mt-1 text-xs opacity-90">
            {
              DEV_IMAGE_VALIDATION_PREVIEW_COPY[devImageValidationPreview]
                .detail
            }
          </p>
        </div>
      ) : null}

      {/* Photo coach + uploader */}
      <SellerPhotoCoach images={images || []} />

      <ImageUploader
        itemId={itemId || ""}
        initialImages={images || []}
        maxImages={maxImages}
        onImageUploaded={handleImageUploaded}
        onImageDeleted={handleImageDeleted}
        onMainImageChanged={handleMainImageChanged}
        forcePermission={forcePermission}
        previewMode={previewMode}
      />

      {/* Empty state message */}
      {!isLoading && (!images || images.length === 0) && (
        <div className="text-center py-4 bg-secondary rounded-md">
          <p className="text-text-light text-sm">
            {minImages > 0
              ? `Upload at least ${minImages} images using the area above.`
              : "No images yet. Upload your first image above."}
          </p>
        </div>
      )}
    </div>
  );
}
